import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2, Upload, X } from "lucide-react";
import { PageShell } from "@/components/site/PageShell";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { QuoteTypeBadge } from "@/components/shared/QuoteTypeBadge";
import { PaynowDialog } from "@/components/shared/PaynowDialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, money } from "@/lib/api";
import { BRAND_PHOTOS, CATEGORIES, VEHICLE_SIZES } from "@/lib/constants";

const STEPS = ["Service", "Vehicle", "Condition", "Photos", "Review"];
const sel = "w-full rounded-md border bg-white px-3 py-2 text-sm";

const Field = ({ label, children }) => (
  <label className="block"><span className="text-sm font-medium text-[#1D1E20]">{label}</span><div className="mt-1.5">{children}</div></label>
);

const StepService = ({ services, st, setSt }) => {
  const svc = services.find((s) => s.slug === st.service);
  const list = services.filter((s) => s.category === st.category);
  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-3 gap-3" data-testid="quote-category-select">
        {CATEGORIES.map((c) => (
          <button key={c.key} type="button" data-testid={`quote-category-${c.key}`} onClick={() => setSt({ ...st, category: c.key, service: "", addons: [] })}
            className={`text-left rounded-xl border p-4 transition-colors ${st.category === c.key ? "border-[#F86900] bg-[#F86900]/5" : "hover:bg-black/5"}`}>
            <p className="font-display font-bold">{c.title}</p><p className="text-xs text-[#56585E] mt-1">{c.tag}</p>
          </button>
        ))}
      </div>
      {st.category && (
        <div className="grid sm:grid-cols-2 gap-2" data-testid="quote-service-select">
          {list.map((s) => (
            <button key={s.slug} type="button" data-testid={`quote-service-${s.slug}`} onClick={() => setSt({ ...st, service: s.slug, addons: [] })}
              className={`flex items-center justify-between gap-2 text-left rounded-xl border px-4 py-3 transition-colors ${st.service === s.slug ? "border-[#020165] bg-[#020165]/5" : "hover:bg-black/5"}`}>
              <span className="text-sm font-semibold">{s.title}</span><QuoteTypeBadge type={s.pricing?.type} />
            </button>
          ))}
        </div>
      )}
      {svc?.addons?.length > 0 && (
        <div><p className="text-sm font-medium mb-2">Optional add-ons</p>
          {svc.addons.map((a) => (
            <label key={a.slug} className="flex items-center gap-3 py-1.5 text-sm" data-testid={`quote-addon-${a.slug}`}>
              <input type="checkbox" checked={st.addons.includes(a.slug)} className="accent-[#F86900] h-4 w-4"
                onChange={(e) => setSt({ ...st, addons: e.target.checked ? [...st.addons, a.slug] : st.addons.filter((x) => x !== a.slug) })} />
              {a.label} <span className="text-[#56585E]">{a.price ? `+${money(a.price)}` : ""}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const StepVehicle = ({ st, setSt }) => {
  const v = st.vehicle;
  const set = (k) => (e) => setSt({ ...st, vehicle: { ...v, [k]: e.target.value } });
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="Make"><Input value={v.make} onChange={set("make")} placeholder="e.g. Toyota" data-testid="quote-vehicle-make-input" /></Field>
      <Field label="Model"><Input value={v.model} onChange={set("model")} placeholder="e.g. Hilux" data-testid="quote-vehicle-model-input" /></Field>
      <Field label="Year"><Input type="number" value={v.year} onChange={set("year")} placeholder="2021" data-testid="quote-vehicle-year-input" /></Field>
      <Field label="Registration / VIN"><Input value={v.registration} onChange={set("registration")} placeholder="e.g. ADX-442" data-testid="quote-vehicle-reg-input" /></Field>
      <Field label="Vehicle size (affects price)">
        <select value={v.size} onChange={set("size")} className={sel} data-testid="quote-vehicle-size-select">
          {VEHICLE_SIZES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </Field>
    </div>
  );
};

const StepCondition = ({ svc, st, setSt }) => {
  const setA = (k, val) => setSt({ ...st, answers: { ...st.answers, [k]: val } });
  return (
    <div className="space-y-4">
      {(svc?.questions || []).map((q) => (
        <Field key={q.slug} label={q.label}>
          {q.type === "select" ? (
            <select value={st.answers[q.slug] || ""} onChange={(e) => setA(q.slug, e.target.value)} className={sel} data-testid={`quote-answer-${q.slug}`}>
              <option value="">Select…</option>{q.options.map((o) => <option key={o}>{o}</option>)}
            </select>
          ) : q.type === "textarea" ? (
            <Textarea rows={3} placeholder={q.placeholder} value={st.answers[q.slug] || ""} onChange={(e) => setA(q.slug, e.target.value)} data-testid={`quote-answer-${q.slug}`} />
          ) : (
            <Input type={q.type === "number" ? "number" : "text"} value={st.answers[q.slug] || ""} onChange={(e) => setA(q.slug, e.target.value)} data-testid={`quote-answer-${q.slug}`} />
          )}
        </Field>
      ))}
      <Field label="Anything else we should know?">
        <Textarea rows={3} value={st.notes} onChange={(e) => setSt({ ...st, notes: e.target.value })} placeholder="Warning lights, fault codes, when the issue happens…" data-testid="quote-notes-input" />
      </Field>
    </div>
  );
};

const StepPhotos = ({ st, setSt }) => {
  const [busy, setBusy] = useState(false);
  const onFiles = async (files) => {
    setBusy(true);
    const added = [];
    for (const file of Array.from(files).slice(0, 8 - st.photos.length)) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("purpose", "quote_photo");
      try {
        const r = await api.post("/files/upload", fd);
        added.push({ ...r.data, preview: URL.createObjectURL(file) });
      } catch (e) {
        toast.error(`${file.name}: ${errMsg(e, "upload failed")}`);
      }
    }
    setSt((s) => ({ ...s, photos: [...s.photos, ...added] }));
    setBusy(false);
  };
  return (
    <div>
      <label data-testid="quote-file-upload-dropzone" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); onFiles(e.dataTransfer.files); }}
        className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#020165]/25 bg-[#020165]/[0.03] py-10 cursor-pointer hover:border-[#F86900] transition-colors">
        {busy ? <Loader2 className="h-6 w-6 animate-spin text-[#020165]" /> : <Upload className="h-6 w-6 text-[#020165]" />}
        <span className="text-sm font-semibold">Drop photos here or tap to choose</span>
        <span className="text-xs text-[#56585E]">Optional · up to 8 · JPG, PNG, WEBP · 10 MB each</span>
        <input type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => onFiles(e.target.files)} data-testid="quote-file-input" />
      </label>
      <div className="mt-4 grid grid-cols-4 gap-2">
        {st.photos.map((p) => (
          <div key={p.id} className="relative aspect-square overflow-hidden rounded-lg" data-testid={`quote-photo-${p.id}`}>
            <img src={p.preview} alt="" className="h-full w-full object-cover" />
            <button type="button" onClick={() => setSt({ ...st, photos: st.photos.filter((x) => x.id !== p.id) })} className="absolute top-1 right-1 rounded-full bg-black/60 p-1 text-white"><X className="h-3 w-3" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

const Breakdown = ({ est }) => (
  <div data-testid="quote-estimate-breakdown">
    <div className="flex items-center justify-between"><h3 className="font-display font-bold text-lg">Itemised estimate</h3><QuoteTypeBadge type={est.state} testId="quote-estimate-state" /></div>
    <div className="mt-3 divide-y text-sm">
      {est.items.map((i, idx) => (
        <div key={idx} className="flex justify-between py-2"><span>{i.label}{i.note && <span className="block text-xs text-[#56585E]">{i.note}</span>}</span><span className="font-semibold">{i.unit_price ? money(i.total) : i.range ? i.range : "TBC"}</span></div>
      ))}
    </div>
    <div className="mt-3 rounded-xl bg-[#020165] text-white p-4 text-sm space-y-1" data-testid="quote-estimate-totals">
      {est.state === "instant" && <><div className="flex justify-between text-lg font-display font-extrabold"><span>Total</span><span data-testid="quote-total">{money(est.totals.total)}</span></div>
        <div className="flex justify-between text-white/75"><span>Deposit ({est.deposit_percent}%)</span><span>{money(est.totals.deposit)}</span></div></>}
      {est.state === "range" && <div className="flex justify-between text-lg font-display font-extrabold"><span>Indicative</span><span data-testid="quote-total">{money(est.totals.total_min)} – {money(est.totals.total_max)}</span></div>}
      {est.state === "assessment" && <p data-testid="quote-total">We need to see the vehicle before pricing. A service advisor will issue a written quote after assessment.</p>}
      <p className="text-white/55 text-xs pt-1">Valid {est.validity_days} days. {est.disclaimer}</p>
    </div>
    {[...est.assumptions, ...est.assessment_reasons].map((a) => <p key={a} className="mt-2 text-xs text-[#56585E]">• {a}</p>)}
  </div>
);

export default function QuoteWizard() {
  const [sp] = useSearchParams();
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [step, setStep] = useState(0);
  const [st, setSt] = useState({ category: "", service: "", addons: [], vehicle: { make: "", model: "", year: "", registration: "", size: "sedan" }, answers: {}, notes: "", photos: [] });
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [est, setEst] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [paid, setPaid] = useState(null);
  const svc = useMemo(() => services.find((s) => s.slug === st.service), [services, st.service]);

  useEffect(() => {
    api.get("/services").then((r) => {
      setServices(r.data.services);
      const pre = r.data.services.find((s) => s.slug === sp.get("service"));
      if (pre) setSt((x) => ({ ...x, category: pre.category, service: pre.slug }));
    });
  }, [sp]);
  useEffect(() => { if (user) setContact((c) => ({ name: c.name || user.name || "", email: c.email || user.email, phone: c.phone || user.phone || "" })); }, [user]);

  const vehiclePayload = () => ({ ...st.vehicle, year: st.vehicle.year ? Number(st.vehicle.year) : null });
  useEffect(() => {
    if (step !== 4 || !st.service) return;
    api.post("/quotes/estimate", { service_slug: st.service, vehicle: vehiclePayload(), addons: st.addons, answers: st.answers }).then((r) => setEst(r.data.estimate));
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const canNext = [!!st.service, !!(st.vehicle.make && st.vehicle.model), true, true][step];

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/quotes", { service_slug: st.service, vehicle: vehiclePayload(), addons: st.addons, answers: st.answers, notes: st.notes,
        photo_ids: st.photos.map((p) => p.id), customer_name: contact.name, customer_email: contact.email, customer_phone: contact.phone || null });
      setResult({ ...r.data.quote, account_created: r.data.account_created });
      toast.success(`Quote request ${r.data.reference} submitted`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const payDeposit = async (method) => {
    const r = await api.post(`/quotes/${result.reference}/deposit-simulate`, { email: result.customer_email, method });
    setPaid(r.data.transaction);
    toast.success(`Simulated deposit paid — ${r.data.transaction.tx_ref}`);
  };

  return (
    <PageShell testId="quote-page" overline="Quote wizard" title="Get a quote in five steps." image={BRAND_PHOTOS[0]}
      intro="Instant prices where we can, honest ranges where we can't, and an assessment when only an inspection will do.">
      <div className="max-w-4xl mx-auto rounded-2xl bg-white p-5 md:p-8" data-testid="quote-wizard-container">
        <ol className="flex gap-2 mb-8">
          {STEPS.map((s, i) => (
            <li key={s} data-testid={`quote-wizard-step-${i + 1}`} className="flex-1">
              <div className={`h-1.5 rounded-full ${i <= step ? "bg-[#F86900]" : "bg-black/10"}`} />
              <span className={`mt-2 block text-xs ${i === step ? "font-bold text-[#0D141A]" : "text-[#56585E]"}`}><span className="hidden sm:inline">{i + 1}. </span>{s}</span>
            </li>
          ))}
        </ol>
        {result ? (
          <div data-testid="quote-success" className="space-y-5">
            <div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-[#027A48] flex items-center justify-center text-white"><Check /></div>
              <div><h2 className="font-display text-2xl font-bold">Request received</h2><p className="text-sm text-[#56585E]">Reference <strong data-testid="quote-reference">{result.reference}</strong> · confirmation sent to {result.customer_email}</p></div></div>
            <Breakdown est={result.estimate} />
            {!user && <p className="rounded-lg bg-[#F4F4F4] px-4 py-3 text-sm text-[#0D141A]" data-testid="quote-account-notice">{result.account_created
              ? <>We created a client portal account for <strong>{result.customer_email}</strong> and emailed you a temporary password. <Link to="/login?next=%2Fportal%2Fquotes" className="underline font-semibold">Sign in</Link> to follow this quote.</>
              : <>This quote was added to your client portal account for <strong>{result.customer_email}</strong>. <Link to="/login?next=%2Fportal%2Fquotes" className="underline font-semibold">Sign in</Link> to see it.</>}</p>}
            <div className="flex flex-wrap gap-3">
              {result.estimate.state !== "assessment" && !paid && (
                <button onClick={() => setPayOpen(true)} data-testid="quote-paynow-deposit-button" className="rounded-full bg-[#F86900] text-[#0D141A] font-bold px-6 py-3 hover:bg-[#ff7d1f] transition-colors">
                  Pay deposit with Paynow
                </button>
              )}
              {paid && <span className="rounded-full bg-[#027A48]/10 text-[#027A48] px-4 py-3 text-sm font-semibold" data-testid="quote-deposit-paid">Deposit paid (simulated) · {paid.tx_ref}</span>}
              <Link to={`/book?service=${result.service_slug}&quote=${result.reference}`} data-testid="quote-book-now-button" className="rounded-full bg-[#020165] text-white font-semibold px-6 py-3 hover:bg-[#0a0a8a] transition-colors">Reserve a workshop slot</Link>
              {!user && <Link to="/login?next=%2Fportal%2Fquotes" className="rounded-full border px-6 py-3 text-sm font-semibold hover:bg-black/5" data-testid="quote-signin-link">Sign in to track it</Link>}
            </div>
            <PaynowDialog open={payOpen} onOpenChange={setPayOpen} reference={result.reference}
              amount={result.estimate.totals.deposit || result.estimate.totals.deposit_min} onPay={payDeposit} />
          </div>
        ) : (
          <>
            {step === 0 && <StepService services={services} st={st} setSt={setSt} />}
            {step === 1 && <StepVehicle st={st} setSt={setSt} />}
            {step === 2 && <StepCondition svc={svc} st={st} setSt={setSt} />}
            {step === 3 && <StepPhotos st={st} setSt={setSt} />}
            {step === 4 && (
              <div className="space-y-6">
                {est ? <Breakdown est={est} /> : <Loader2 className="h-5 w-5 animate-spin" />}
                <div className="grid sm:grid-cols-3 gap-3">
                  <Input placeholder="Your name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="quote-contact-name-input" />
                  <Input type="email" placeholder="Email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="quote-contact-email-input" />
                  <Input placeholder="Phone" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} data-testid="quote-contact-phone-input" />
                </div>
              </div>
            )}
            <div className="mt-8 flex justify-between">
              <button type="button" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="quote-wizard-back-button" className="rounded-full border px-6 py-2.5 text-sm font-semibold disabled:opacity-40 hover:bg-black/5">Back</button>
              {step < 4 ? (
                <button type="button" disabled={!canNext} onClick={() => setStep(step + 1)} data-testid="quote-wizard-next-button" className="rounded-full bg-[#020165] text-white px-7 py-2.5 text-sm font-semibold disabled:opacity-40 hover:bg-[#0a0a8a] transition-colors">Continue</button>
              ) : (
                <button type="button" disabled={busy || !contact.name || !contact.email} onClick={submit} data-testid="quote-wizard-submit-button" className="rounded-full bg-[#F86900] text-[#0D141A] px-7 py-2.5 text-sm font-bold disabled:opacity-40 hover:bg-[#ff7d1f] transition-colors">{busy ? "Submitting…" : "Submit quote request"}</button>
              )}
            </div>
          </>
        )}
      </div>
    </PageShell>
  );
}
